/**
 * src/utils/requirementRatings.js
 *
 * Maturity ratings are carried by the framework REQUIREMENT, not by the TCF
 * control (CTRL-B-001).
 *
 * Why: a requirement (ISO 27001 clause 6.3) is what an auditor asks about and
 * what a named person answers for. A TCF control is the implementation that
 * answers several requirements at once. Storing the rating on the control made
 * those requirements one record drawn as N rows — across the 14 framework
 * files 508 mappings collide this way, so rating ISO clause 6.3 also rated
 * clause 8.1, and the justification typed against one appeared under the
 * other. `effectivenessScale.js` already states the matching rule for the
 * goal: "the pair (framework, control) is what carries a goal … there is no
 * single answer" for a control alone. The rating now follows the same grain.
 *
 * The control keeps everything genuinely entered once — evidence, mapped
 * documents, obligations, and the system-side gate checks in evaluateControl.
 * Only the owner's own judgement moves to the requirement.
 *
 * ── A carry is a suggestion, never a value (CTRL-B-002) ──────────────────────
 *
 * A customer who rated ISO 27001's context clauses and then opens ISO 22301,
 * whose context clauses map to the same control, should not retype anything.
 * But the module must never claim Tryggr knows a company's maturity, so a
 * rating that no person recorded against THIS requirement is offered as a
 * suggestion to adopt — never returned as its score.
 *
 * This matters beyond the screen. Four readers outside MaturityView consume
 * the effectiveness map as asserted fact and cannot see a badge: the framework
 * maturity percentage (computeFrameworkMaturity), the assurance case's §9.1
 * evidence node, the Workbench maturity column, and the tenant's own audit
 * export. Provenance therefore lives in the stored record — a suggestion is
 * simply never written there. Adopting one writes a real assessment that
 * carries the source's justification, author and date forward.
 *
 * Suggestions are cross-framework only. Two requirements in the SAME framework
 * never suggest to each other, however coarse the mapping between them — that
 * is precisely the defect this module exists to remove. ISO 6.3 and ISO 8.1
 * share SCF-MSS-004 and must still be answered separately.
 */

const KEY_SEPARATOR = '::';

/**
 * Requirement key: `${frameworkId}::${externalId}`.
 *
 * Same composite shape the SoA entity rows already use, and split the same way
 * — on the FIRST separator only. Framework ids are slugs (fw-iso27001) that
 * never contain '::', while an external control id is framework-supplied and
 * is not ours to constrain.
 */
export function requirementKey(frameworkId, externalId) {
  if (!frameworkId || !externalId) return null;
  if (String(frameworkId).includes(KEY_SEPARATOR)) {
    throw new Error(`framework id must not contain "${KEY_SEPARATOR}": ${frameworkId}`);
  }
  return `${frameworkId}${KEY_SEPARATOR}${externalId}`;
}

export function parseRequirementKey(key) {
  if (typeof key !== 'string') return null;
  const at = key.indexOf(KEY_SEPARATOR);
  if (at <= 0) return null;
  const externalId = key.slice(at + KEY_SEPARATOR.length);
  if (!externalId) return null;
  return { frameworkId: key.slice(0, at), externalId };
}

/** True for a key written by this module, false for a bare control id. */
export function isRequirementKey(key) {
  return parseRequirementKey(key) !== null;
}

export const RATING_SOURCE = {
  /** A person rated this requirement. The only source that is a score. */
  EXPLICIT: 'explicit',
  /** Nobody has answered for this requirement. There may still be a suggestion. */
  NONE: 'none',
};

export const SUGGESTION_KIND = {
  /** The same control, rated on requirements in other frameworks. */
  SIBLING: 'sibling',
  /** A pre-split rating stored against the TCF control itself. */
  LEGACY_CONTROL: 'legacy-control',
};

/**
 * Index every requirement across the active frameworks once per state change.
 *
 * `frameworks` are the enriched framework objects — each carries `mappings`
 * (post-TCF merge) or `controls` (enrichFrameworksWithControls output).
 */
export function buildRequirementIndex(frameworks) {
  const byKey = new Map();
  const keysByScfId = new Map();
  (frameworks || []).forEach((fw) => {
    const rows = fw?.mappings || fw?.controls || [];
    rows.forEach((row) => {
      const externalId = row?.externalId || row?.id;
      const scfId = row?.scfId || null;
      const key = requirementKey(fw.id, externalId);
      if (!key) return;
      // A requirement may be answered by SEVERAL controls — 180 of them are,
      // across 8 frameworks (ISO 42001 clause 5.2 is answered by both the
      // information-security policy control and the AI policy control). It is
      // still ONE requirement with one answer, so the entry accumulates its
      // controls rather than the second row creating a second entry.
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          frameworkId: fw.id,
          frameworkName: fw.shortName || fw.name || fw.id,
          externalId,
          scfIds: [],
          title: row?.title || null,
        });
      }
      const meta = byKey.get(key);
      if (!meta.title && row?.title) meta.title = row.title;
      if (!scfId || meta.scfIds.includes(scfId)) return;
      meta.scfIds.push(scfId);
      if (!keysByScfId.has(scfId)) keysByScfId.set(scfId, []);
      keysByScfId.get(scfId).push(key);
    });
  });
  return { byKey, keysByScfId };
}

function ratedEntry(effectiveness, key) {
  const entry = effectiveness?.[key];
  return entry && Number.isFinite(entry.score) ? entry : null;
}

function summarise(kind, sources) {
  const scores = sources.map((s) => s.score);
  const sum = scores.reduce((a, b) => a + b, 0);
  return {
    kind,
    /** The average the founder asked for — a starting point a person adopts. */
    score: sum / scores.length,
    min: Math.min(...scores),
    max: Math.max(...scores),
    sources,
  };
}

/**
 * What to offer for a requirement nobody has rated. Null when there is
 * nothing to carry.
 *
 * Sibling ratings win over a pre-split control-level rating: a rating made
 * against a named clause is a better answer than one made against the merged
 * control, and it is newer.
 */
function buildSuggestion(key, meta, index, effectiveness) {
  if (!meta?.scfIds?.length) return null;
  const siblingKeys = new Set();
  meta.scfIds.forEach((scfId) =>
    (index.keysByScfId.get(scfId) || []).forEach((k) => siblingKeys.add(k))
  );
  const siblings = [...siblingKeys]
    .filter((k) => {
      if (k === key) return false;
      // Cross-framework only. A sibling clause in the same framework is a
      // separate question, never an answer to this one.
      return index.byKey.get(k)?.frameworkId !== meta.frameworkId;
    })
    .map((k) => {
      const entry = ratedEntry(effectiveness, k);
      if (!entry) return null;
      const sib = index.byKey.get(k);
      return {
        key: k,
        frameworkId: sib.frameworkId,
        frameworkName: sib.frameworkName,
        externalId: sib.externalId,
        score: entry.score,
        justification: entry.justification || '',
        ratedAt: entry.ratedAt || null,
        ratedBy: entry.ratedBy || null,
      };
    })
    .filter(Boolean);

  if (siblings.length) return summarise(SUGGESTION_KIND.SIBLING, siblings);

  // Pre-split ratings were made against the control, so they cannot say which
  // of its requirements the person had in mind. They are somebody's real
  // judgement and are not discarded — they are offered for adoption on each
  // covered requirement, which is the only act that can answer that question.
  // Where several controls answer this requirement and more than one carries a
  // pre-split rating, the lowest is offered: they were separate judgements
  // about separate controls, and the clause is only as strong as the weakest
  // of them.
  const legacyEntries = meta.scfIds
    .map((scfId) => ({ scfId, entry: ratedEntry(effectiveness, scfId) }))
    .filter((l) => l.entry)
    .sort((a, b) => a.entry.score - b.entry.score);
  const legacy = legacyEntries[0];
  if (!legacy) return null;
  return summarise(SUGGESTION_KIND.LEGACY_CONTROL, [
    {
      key: legacy.scfId,
      frameworkId: null,
      frameworkName: null,
      externalId: legacy.scfId,
      score: legacy.entry.score,
      justification: legacy.entry.justification || '',
      ratedAt: legacy.entry.ratedAt || null,
      ratedBy: legacy.entry.ratedBy || null,
    },
  ]);
}

/**
 * Resolve what to show for one requirement.
 *
 * Returns `{ score, source, entry, suggestion }`:
 *   score       the rated level, or null when nobody has answered. A
 *               suggestion never fills this in — see CTRL-B-002.
 *   source      RATING_SOURCE.EXPLICIT or RATING_SOURCE.NONE.
 *   entry       the stored rating when explicit, else null.
 *   suggestion  what could be adopted in one click, or null.
 */
export function resolveRequirementRating(key, index, effectiveness) {
  const own = ratedEntry(effectiveness, key);
  if (own) {
    return { score: own.score, source: RATING_SOURCE.EXPLICIT, entry: own, suggestion: null };
  }
  const meta = index?.byKey?.get(key);
  return {
    score: null,
    source: RATING_SOURCE.NONE,
    entry: null,
    suggestion: buildSuggestion(key, meta, index, effectiveness),
  };
}

/** Only an explicit rating is an assessment — see the module note. */
export function isAssessed(resolved) {
  return resolved?.source === RATING_SOURCE.EXPLICIT;
}

/**
 * Turn a suggestion into the rating to write when a person adopts it.
 *
 * The score rounds DOWN: the scale is whole levels, and an average of 2 and 3
 * must not become the 3 that meets a goal of 3. The justification names where
 * the figure came from, so the adopted assessment carries its own provenance
 * in the field an auditor reads — `justification` is what exportData and the
 * assurance case surface, and a blank one there is the claim-without-basis
 * this module must never make.
 */
export function buildAdoptedRating(suggestion, { userId = null, at = null } = {}) {
  if (!suggestion?.sources?.length) return null;
  const score = Math.floor(suggestion.score);
  if (!Number.isFinite(score) || score < 1) return null;
  const cited = suggestion.sources
    .map((s) => {
      const where = s.frameworkName ? `${s.frameworkName} ${s.externalId}` : s.externalId;
      return s.justification
        ? `${where} (rated ${s.score}): ${s.justification}`
        : `${where}: rated ${s.score}`;
    })
    .join('\n');
  const lead =
    suggestion.kind === SUGGESTION_KIND.LEGACY_CONTROL
      ? 'Adopted from the rating previously held against the shared control.'
      : 'Adopted from the same control rated under another framework.';
  // Provenance rides in `justification` deliberately, and only there. That is
  // the field the audit export and the assurance case surface, so it is the
  // one an auditor actually reads; a structured `adoptedFrom` alongside it
  // would be dropped by the write path (commitRating and controls-write both
  // persist score/justification/ratedAt/ratedBy and nothing else) and would
  // be a field the product does not keep.
  return {
    score,
    justification: `${lead}\n${cited}`,
    ratedAt: at || new Date().toISOString(),
    ratedBy: userId,
  };
}
